import { useState } from "react";
import { Stage, Layer, Line, Circle, Group, Rect, Text } from "react-konva";
import TokenSprite from "./TokenSprite";
import ObstacleCreateDialog from "./ObstacleCreateDialog";
import TemplateEditDialog from "./TemplateEditDialog";
import DoorCreateDialog from "./DoorCreateDialog";
import DoorLockModal from "./DoorLockModal";
import NpcInteractModal from "./NpcInteractModal";
import AttackModal, { buildAttackSources } from "./AttackModal";
import ChestLootModal from "./ChestLootModal";
import { MapToolbar } from "./mapcanvas/MapToolbar";
import { MapGridBackground } from "./mapcanvas/MapGridBackground";
import { MapTemplatesLayer } from "./mapcanvas/MapTemplatesLayer";
import { MapCombatOverlays } from "./mapcanvas/MapCombatOverlays";
import { MapDoorsLayer } from "./mapcanvas/MapDoorsLayer";
import { MapWallsLayer } from "./mapcanvas/MapWallsLayer";
import { MapObstaclesLayer } from "./mapcanvas/MapObstaclesLayer";
import { useMapViewport } from "../../hooks/useMapViewport";
import { useMapVision } from "../../hooks/useMapVision";
import { useMapDrawingTools } from "../../hooks/useMapDrawingTools";
import { useKonvaImage } from "../../hooks/useKonvaImage";
import { hasLineOfSight, segmentBlockedByWalls } from "../../lib/visibility";
import { api } from "../../lib/api";
import { useGameStore } from "../../store/gameStore";
import { toast } from "sonner";
import { playSfxByTag, playSfxAsset } from "../../lib/soundBus";
import TokenContextMenu from "./TokenContextMenu";
import { MapPin } from "lucide-react";

export default function MapCanvas({ game, scene, tokens, templates, obstacles, walls, doors, myMemberId, isGm, mySpeedFt, memberIdToUserId, character, stats, onCharacterUpdate }) {
  const { containerRef, dims, scale, isTouchDevice, handleWheel, handleTouchStart, handleTouchMove, handleTouchEnd } = useMapViewport();
  const [obstacleDialog, setObstacleDialog] = useState(null); // { pos } for circle create, { points } for line create, or { existing } for edit
  const [doorDialog, setDoorDialog] = useState(null); // { pos } for create, or { existing } for edit
  const [arrivalPick, setArrivalPick] = useState(null); // { returnSceneId } while GM is picking a door's arrival point on another map
  const [pickedArrival, setPickedArrival] = useState(null); // { x, y } captured from the destination map click
  const [lockDoorTarget, setLockDoorTarget] = useState(null); // the locked door a player just clicked
  const [templateEditing, setTemplateEditing] = useState(null); // the template object being edited
  const [interactingNpc, setInteractingNpc] = useState(null);
  const [shoppingTokenId, setShoppingTokenId] = useState(null);
  const [attackTarget, setAttackTarget] = useState(null);
  const [openingChest, setOpeningChest] = useState(null);
  const [contextMenuTargetId, setContextMenuTargetId] = useState(null);
  const [movingTokenId, setMovingTokenId] = useState(null);
  // { tokenId, x, y } while a token is actively being dragged — lets the light/vision holes
  // (and anything else keyed to a token's position) track the cursor live, instead of waiting
  // for the PATCH + WebSocket round-trip to land (which visibly lags behind on mobile/higher
  // latency connections).
  const [dragPreview, setDragPreview] = useState(null);
  const mapImage = useKonvaImage(scene?.map_asset_url);
  const upsertObstacleLocal = useGameStore((s) => s.upsertObstacle);
  const removeObstacleLocal = useGameStore((s) => s.removeObstacle);
  const upsertTemplateLocal = useGameStore((s) => s.upsertTemplate);
  const removeTemplateLocal = useGameStore((s) => s.removeTemplate);
  const upsertDoorLocal = useGameStore((s) => s.upsertDoor);
  const removeDoorLocal = useGameStore((s) => s.removeDoor);
  const selectedTokenId = useGameStore((s) => s.selectedTokenId);
  const setSelectedTokenId = useGameStore((s) => s.setSelectedTokenId);
  const setActiveScene = useGameStore((s) => s.setActiveScene);
  const freeRoam = useGameStore((s) => s.free_roam);

  const gridSize = scene?.grid_size || 50;
  const width = scene?.width || 1600;
  const height = scene?.height || 1000;

  const { myTokens, canSeeToken, renderFogLayer, renderDarknessTint } =
    useMapVision({ game, scene, tokens, walls, myMemberId, isGm, gridSize, width, height, dragPreview });

  const startArrivalPick = (targetSceneId) => {
    if (!targetSceneId || !scene?.id) return;
    setArrivalPick({ returnSceneId: scene.id });
    setActiveScene(targetSceneId);
  };

  const cancelArrivalPick = () => {
    if (arrivalPick) setActiveScene(arrivalPick.returnSceneId);
    setArrivalPick(null);
  };

  const {
    activeTool, switchTool, clearTool,
    ruler, wallDraft, templateDraft, triggerLineDraft,
    selectedWallId, setSelectedWallId,
    selectedTemplateId, setSelectedTemplateId,
    rulerDistanceFt, handleStageClick, handleStageMouseMove, handleStageDblClick, handleOverlayClick,
  } = useMapDrawingTools({
    game, scene, gridSize, arrivalPick,
    onArrivalPick: (pos) => { setPickedArrival(pos); setActiveScene(arrivalPick.returnSceneId); setArrivalPick(null); },
    onOpenObstacleDialog: setObstacleDialog,
    onOpenDoorDialog: setDoorDialog,
    onEmptyClick: () => { setSelectedTokenId(null); setContextMenuTargetId(null); },
  });

  const handleDragMove = (tokenId, x, y) => setDragPreview({ tokenId, x, y });

  const handleDragEnd = async (tokenId, x, y, konvaNode) => {
    let finalX = x, finalY = y;
    const token = tokens.find((t) => t.id === tokenId);
    const draggedToken = tokens.find((t) => t.dragged_by_token_id === tokenId);

    if (freeRoam?.enabled && token?.created_by === myMemberId) {
      const myEncounter = (freeRoam.encounters || []).find((e) => e.order?.some((o) => o.member_id === myMemberId));
      if (myEncounter && myEncounter.order[myEncounter.turn_index]?.member_id !== myMemberId) {
        konvaNode?.position({ x: token.x, y: token.y });
        konvaNode?.getLayer()?.batchDraw();
        toast.error("Wait for your turn in this fight!");
        setDragPreview(null);
        return;
      }
    }

    if (game?.speed_restrictions_enabled && token) {
      let capFt = token.created_by === myMemberId && mySpeedFt ? mySpeedFt : token.speed_ft ?? 25;
      if (draggedToken) capFt = capFt / 2;
      const dx = x - token.x, dy = y - token.y;
      const distFt = (Math.hypot(dx, dy) / gridSize) * 5;
      if (distFt > capFt) {
        const maxPx = (capFt / 5) * gridSize;
        const factor = maxPx / Math.hypot(dx, dy);
        finalX = token.x + dx * factor;
        finalY = token.y + dy * factor;
        toast.warning(`${token.name} capped at ${capFt}ft speed${draggedToken ? ` (dragging ${draggedToken.name})` : ""}`);
      }
    }
    // Walls physically block movement (not just line of sight) — applied identically whether
    // the drag came from a desktop mouse or the mobile Move->Drag->Done flow, since both funnel
    // through this same handler.
    const wallStop = segmentBlockedByWalls({ x: token.x, y: token.y }, { x: finalX, y: finalY }, walls);
    if (wallStop) {
      finalX = wallStop.x;
      finalY = wallStop.y;
      konvaNode?.position({ x: finalX, y: finalY });
      konvaNode?.getLayer()?.batchDraw();
      toast.warning(`${token.name} bumped into a wall`);
    }
    // Keep tracking the token's FINAL resting spot (not the stale store value) until the
    // PATCH + WebSocket round-trip actually lands, instead of snapping the light/vision hole
    // back to the old position for that one round-trip window.
    setDragPreview({ tokenId, x: finalX, y: finalY });
    const appliedDx = finalX - token.x, appliedDy = finalY - token.y;
    try {
      await api.patch(`/games/${game.id}/tokens/${tokenId}`, { x: finalX, y: finalY });
      playSfxByTag(game.id, "footstep");
      if (draggedToken) {
        await api.patch(`/games/${game.id}/tokens/${draggedToken.id}`, { x: draggedToken.x + appliedDx, y: draggedToken.y + appliedDy });
      }
      if (freeRoam?.enabled && token?.created_by === myMemberId) {
        const distFt = (Math.hypot(appliedDx, appliedDy) / gridSize) * 5;
        if (distFt > 0) api.post(`/games/${game.id}/free-roam/movement`, { member_id: myMemberId, distance_ft: distFt }).catch(() => {});
      }
    } catch {
      toast.error("Could not move token");
    } finally {
      setDragPreview(null);
    }
  };

  const startDragging = async (myToken, target) => {
    try {
      await api.post(`/games/${game.id}/tokens/${target.id}/drag`, { dragger_token_id: myToken.id });
      toast.success(`Now dragging ${target.name} (half speed while towing)`);
    } catch {
      toast.error("Could not start dragging");
    }
  };

  const stopDragging = async (target) => {
    try {
      await api.post(`/games/${game.id}/tokens/${target.id}/drag`, { dragger_token_id: null });
    } catch {
      toast.error("Could not release");
    }
  };

  const tieUp = async (target) => {
    if (!character) return toast.error("You need a character to tie someone up");
    try {
      const { data } = await api.post(`/characters/${character.id}/tie-up`, { game_id: game.id, token_id: target.id });
      onCharacterUpdate?.(data.character);
      toast.success(`${target.name} is tied up (escape DC ${data.token.restrain_dc})`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not tie them up");
    }
  };

  const openNpc = (t) => {
    setInteractingNpc(t);
    if (t.greet_sfx_asset_id) playSfxAsset(t.greet_sfx_asset_id);
    if (t.is_shopkeeper) {
      const myToken = findMyNearbyToken(t);
      if (myToken) {
        setShoppingTokenId(myToken.id);
        api.patch(`/games/${game.id}/tokens/${myToken.id}`, { is_shopping: true }).catch(() => {});
      }
    }
  };

  const closeNpc = () => {
    setInteractingNpc(null);
    if (shoppingTokenId) {
      api.patch(`/games/${game.id}/tokens/${shoppingTokenId}`, { is_shopping: false }).catch(() => {});
      setShoppingTokenId(null);
    }
  };

  const handleTemplateDragEnd = async (templateId, x, y) => {
    await api.patch(`/games/${game.id}/templates/${templateId}`, { x, y });
  };

  const canEditToken = (token) => isGm || token.created_by === myMemberId;

  // Desktop keeps free drag for any token you can edit; touch devices (GM included) require
  // explicitly entering "Move" mode first (via the pill above the token) so a stray tap doesn't
  // yank it — without this, a one-finger tap on a touchscreen is ambiguous between "select" and
  // "drag", and fights the map's own pan gesture (this previously only applied to players, which
  // is why a GM testing on a phone/tablet saw every tap on every token misbehave).
  // A locked token can never be dragged by anyone (including the GM) until unlocked again.
  const draggableFor = (token) => {
    if (token.locked) return false;
    if (!canEditToken(token)) return false;
    if (!isTouchDevice) return true;
    return movingTokenId === token.id;
  };

  const toggleTokenLock = async (token) => {
    try {
      await api.patch(`/games/${game.id}/tokens/${token.id}`, { locked: !token.locked });
    } catch {
      toast.error("Could not toggle lock");
    }
  };

  const handleTokenTap = (t) => {
    const isOwn = t.created_by === myMemberId;
    if (isGm || isOwn) {
      setSelectedTokenId(t.id);
      setContextMenuTargetId(null);
      return;
    }
    if (t.token_type === "light" && !t.light_pickable) return; // decorative, non-pickable lights aren't interactive for players
    if (contextMenuTargetId === t.id) {
      setContextMenuTargetId(null);
      return;
    }
    const myToken = findMyNearbyToken(t);
    if (!myToken) {
      toast.error("You need a token on this map first");
      return;
    }
    if (!hasLineOfSight({ x: myToken.x, y: myToken.y }, { x: t.x, y: t.y }, walls)) {
      toast.error("No line of sight to that");
      return;
    }
    const actions = buildTargetActions(t, myToken);
    if (actions.length === 0) {
      toast.error("Nothing to do there right now");
      return;
    }
    setSelectedTokenId(null);
    setContextMenuTargetId(t.id);
  };

  // Builds the hovering context menu's actions for a clicked token — options depend on the
  // token's type/state, already gated by the LoS + range check that ran before this is called.
  const buildTargetActions = (t, myToken) => {
    const distFt = (Math.hypot(t.x - myToken.x, t.y - myToken.y) / gridSize) * 5;
    const actions = [];
    if (t.token_type === "npc") {
      const alive = t.hp_current == null || t.hp_current > 0;
      if (alive) {
        actions.push({
          label: "Attack", testId: `ctx-attack-${t.id}`, danger: true,
          onClick: () => {
            if (myToken.weapon_drawn === false) {
              toast.error(game?.system === "dnd5e" ? "Draw your weapon first (a free object interaction)" : "Draw your weapon first (1 Interact action)");
              return;
            }
            setAttackTarget(t);
            setContextMenuTargetId(null);
          },
        });
        if (distFt <= 20) {
          actions.push({ label: "Talk", testId: `ctx-talk-${t.id}`, onClick: () => { openNpc(t); setContextMenuTargetId(null); } });
        }
      } else {
        actions.push({ label: "Loot", testId: `ctx-loot-${t.id}`, onClick: () => { setOpeningChest(t); playSfxByTag(game.id, "chest_open"); setContextMenuTargetId(null); } });
        if (!t.restrained) actions.push({ label: "Tie Up", testId: `ctx-tie-${t.id}`, onClick: () => { tieUp(t); setContextMenuTargetId(null); } });
        const isDraggingThis = t.dragged_by_token_id === myToken.id;
        actions.push(
          isDraggingThis
            ? { label: "Stop Dragging", testId: `ctx-stop-drag-${t.id}`, onClick: () => { stopDragging(t); setContextMenuTargetId(null); } }
            : { label: "Drag", testId: `ctx-drag-${t.id}`, onClick: () => { startDragging(myToken, t); setContextMenuTargetId(null); } }
        );
      }
    } else if (t.token_type === "chest") {
      actions.push({ label: "Loot", testId: `ctx-loot-${t.id}`, onClick: () => { setOpeningChest(t); playSfxByTag(game.id, "chest_open"); setContextMenuTargetId(null); } });
    } else if (t.token_type === "light" && t.light_pickable && distFt <= 10) {
      actions.push({
        label: "Pick Up", testId: `ctx-pickup-light-${t.id}`,
        onClick: async () => {
          try {
            await api.post(`/games/${game.id}/tokens/${t.id}/pickup-light`);
            toast.success(`Picked up ${t.name}`);
          } catch {
            toast.error("Could not pick that up");
          }
          setContextMenuTargetId(null);
        },
      });
    } else if (distFt <= 20) {
      actions.push({
        label: "Talk", testId: `ctx-talk-${t.id}`,
        onClick: () => { api.post(`/games/${game.id}/chat`, { content: `*waves to ${t.name}*` }).catch(() => {}); setContextMenuTargetId(null); },
      });
    }
    return actions;
  };

  const findMyNearbyToken = (t) => myTokens.find((mt) => Math.hypot(mt.x - t.x, mt.y - t.y) <= gridSize * 2) || myTokens[0];

  const nearbyUnlockedDoors = !isGm
    ? (doors || []).filter((d) => !d.locked && !d.broken && myTokens.some((mt) => Math.hypot(mt.x - d.x, mt.y - d.y) <= gridSize * 2))
    : [];

  const openDoor = async (door) => {
    let myToken = myTokens.find((mt) => Math.hypot(mt.x - door.x, mt.y - door.y) <= gridSize * 2);
    if (!myToken && freeRoam?.enabled) {
      // Free Roam convenience: clicking a door that's farther away but still within the
      // player's movement speed walks them over (respecting walls) and opens it in one go,
      // instead of forcing them to manually drag their token there first.
      const candidate = myTokens[0];
      if (candidate) {
        const dx = door.x - candidate.x, dy = door.y - candidate.y;
        const dist = Math.hypot(dx, dy);
        const distFt = (dist / gridSize) * 5;
        const capFt = game?.speed_restrictions_enabled ? (mySpeedFt || candidate.speed_ft || 25) : Infinity;
        if (distFt <= capFt) {
          const standRatio = dist > gridSize ? (dist - gridSize) / dist : 0;
          let finalX = candidate.x + dx * standRatio, finalY = candidate.y + dy * standRatio;
          const wallStop = segmentBlockedByWalls({ x: candidate.x, y: candidate.y }, { x: finalX, y: finalY }, walls);
          if (wallStop) { finalX = wallStop.x; finalY = wallStop.y; }
          try {
            await api.patch(`/games/${game.id}/tokens/${candidate.id}`, { x: finalX, y: finalY });
            playSfxByTag(game.id, "footstep");
            const walkedFt = (Math.hypot(finalX - candidate.x, finalY - candidate.y) / gridSize) * 5;
            if (walkedFt > 0) api.post(`/games/${game.id}/free-roam/movement`, { member_id: myMemberId, distance_ft: walkedFt }).catch(() => {});
            myToken = { ...candidate, x: finalX, y: finalY };
          } catch {
            toast.error("Could not walk to the door");
            return;
          }
        }
      }
    }
    if (!myToken) {
      toast.error("Too far to reach — move closer to the door first");
      return;
    }
    try {
      const { data } = await api.post(`/games/${game.id}/doors/${door.id}/open`, { token_id: myToken.id });
      if (data.mode === "locked") {
        setLockDoorTarget({ door, token: myToken });
      } else if (data.mode === "pending") {
        toast.info("Waiting for the GM to let you through…");
      } else {
        toast.success(`Through the door into a new place`);
      }
    } catch {
      toast.error("Could not open the door");
    }
  };

  const lockDoorBehindMe = async (door) => {
    const myToken = findMyNearbyToken(door);
    if (!myToken) return;
    try {
      await api.post(`/games/${game.id}/doors/${door.id}/lock`, { token_id: myToken.id });
    } catch {
      toast.error("Could not lock the door");
    }
  };

  // Weapon reach ring: while a player has their own token selected, show a faint radius of their
  // longest available attack (melee reach or ranged weapon) and highlight enemies caught in it.
  const selectedToken = tokens.find((t) => t.id === selectedTokenId);
  const showReachRing = !!(selectedToken && selectedToken.created_by === myMemberId && character && stats && selectedToken.weapon_drawn !== false);
  const reachRangeFt = showReachRing ? Math.max(5, ...buildAttackSources(game, character, stats).map((s) => s.range_ft || 5)) : 0;
  const reachRadiusPx = (reachRangeFt / 5) * gridSize;

  return (
    <div ref={containerRef} className="absolute inset-0 bg-[#1a1008]" style={{ touchAction: "none" }} data-testid="map-canvas-container">
      <MapToolbar isGm={isGm} activeTool={activeTool} switchTool={switchTool} clearTool={clearTool} />

      <Stage
        width={dims.width}
        height={dims.height}
        draggable={!activeTool}
        scaleX={scale}
        scaleY={scale}
        onWheel={handleWheel}
        onClick={handleStageClick}
        onTap={handleStageClick}
        onDblClick={handleStageDblClick}
        onDblTap={handleStageDblClick}
        onMouseMove={handleStageMouseMove}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <MapGridBackground game={game} gridSize={gridSize} width={width} height={height} mapImage={mapImage} />
        <Layer>
          <MapTemplatesLayer
            templates={templates}
            activeTool={activeTool}
            isGm={isGm}
            myMemberId={myMemberId}
            selectedTemplateId={selectedTemplateId}
            setSelectedTemplateId={setSelectedTemplateId}
            handleOverlayClick={handleOverlayClick}
            onTemplateDragEnd={handleTemplateDragEnd}
            gameId={game.id}
            setTemplateEditing={setTemplateEditing}
            removeTemplateLocal={removeTemplateLocal}
          />
          <MapCombatOverlays
            showReachRing={showReachRing}
            selectedToken={selectedToken}
            reachRadiusPx={reachRadiusPx}
            reachRangeFt={reachRangeFt}
            tokens={tokens}
            isGm={isGm}
            myMemberId={myMemberId}
            gridSize={gridSize}
          />
          {tokens.filter((t) => canSeeToken(t)).map((t) => (
            <TokenSprite
              key={t.id}
              token={t}
              isSelected={selectedTokenId === t.id}
              isMoving={movingTokenId === t.id}
              onSelect={(e) => { if (!handleOverlayClick(e)) handleTokenTap(t); }}
              onDragMove={handleDragMove}
              onDragEnd={handleDragEnd}
              canEdit={!activeTool && draggableFor(t)}
              canManage={canEditToken(t)}
              onToggleLock={() => toggleTokenLock(t)}
              videoIdentity={memberIdToUserId?.[t.created_by]}
              isActiveTurn={!!(game?.combat?.active && game.combat.order?.[game.combat.turn_index]?.token_id === t.id)}
            />
          ))}
          {ruler && (
            <>
              <Line points={[ruler.start.x, ruler.start.y, ruler.end.x, ruler.end.y]} stroke="#D4AF37" strokeWidth={2} dash={[8, 6]} listening={false} />
              <Circle x={ruler.end.x} y={ruler.end.y} radius={4} fill="#D4AF37" listening={false} />
            </>
          )}
          <MapDoorsLayer doors={doors} isGm={isGm} handleOverlayClick={handleOverlayClick} setDoorDialog={setDoorDialog} openDoor={openDoor} />
        </Layer>
        {isGm && (
          <Layer>
            <MapWallsLayer
              game={game}
              walls={walls}
              selectedWallId={selectedWallId}
              setSelectedWallId={setSelectedWallId}
              activeTool={activeTool}
              handleOverlayClick={handleOverlayClick}
              wallDraft={wallDraft}
              templateDraft={templateDraft}
              triggerLineDraft={triggerLineDraft}
            />
            <MapObstaclesLayer
              obstacles={obstacles}
              activeTool={activeTool}
              handleOverlayClick={handleOverlayClick}
              setObstacleDialog={setObstacleDialog}
              gameId={game.id}
              upsertObstacleLocal={upsertObstacleLocal}
            />
          </Layer>
        )}
        {renderFogLayer()}
        {renderDarknessTint()}
        {/* Interact buttons (Talk/Attack/Loot/Tie-Up/Drag context menu + the "Lock Door behind
            me" quick-action) always render on TOP of the fog/darkness layers — a player who's
            close enough to interact with something should always be able to see and tap that
            button, even if the target itself is visually obscured by darkness/fog. */}
        <Layer>
          {contextMenuTargetId && (() => {
            const target = tokens.find((t) => t.id === contextMenuTargetId);
            if (!target) return null;
            const myToken = findMyNearbyToken(target);
            if (!myToken) return null;
            const actions = buildTargetActions(target, myToken);
            if (actions.length === 0) return null;
            return (
              <TokenContextMenu
                key={`ctx-menu-${target.id}`}
                x={target.x}
                y={target.y - (target.size || 50) / 2 - 10}
                actions={actions}
                token={target}
                testId={`token-context-menu-${target.id}`}
              />
            );
          })()}
          {nearbyUnlockedDoors.map((d) => (
            <Group
              key={`door-lock-${d.id}`}
              data-testid={`door-lock-button-${d.id}`}
              x={d.x - 42}
              y={d.y - 48}
              onClick={(e) => { e.cancelBubble = true; lockDoorBehindMe(d); }}
              onTap={(e) => { e.cancelBubble = true; lockDoorBehindMe(d); }}
            >
              <Rect width={84} height={24} cornerRadius={12} fill="#322216dd" stroke="#D4AF37" strokeWidth={1} />
              <Text text="Lock Door" width={84} height={24} align="center" verticalAlign="middle" fontSize={10} fill="#D4AF37" />
            </Group>
          ))}
        </Layer>
      </Stage>
      {isTouchDevice && (() => {
        const t = tokens.find((tok) => tok.id === (movingTokenId || selectedTokenId));
        if (!t || !canEditToken(t)) return null;
        const moving = movingTokenId === t.id;
        return (
          <button
            data-testid={`mobile-move-toggle-${t.id}`}
            onClick={() => setMovingTokenId(moving ? null : t.id)}
            className={`absolute top-20 left-1/2 -translate-x-1/2 z-30 px-5 py-2 rounded-full border text-xs font-bold uppercase tracking-widest shadow-lg transition-colors duration-200 ${
              moving ? "bg-[#4d7c0f] border-[#4d7c0f] text-white" : "bg-black/70 backdrop-blur-2xl border-[#D4AF37] text-[#D4AF37]"
            }`}
          >
            {moving ? "Done Moving" : `Move ${t.name}`}
          </button>
        );
      })()}
      {ruler && (
        <div className="absolute bottom-48 md:bottom-36 left-1/2 -translate-x-1/2 md:left-auto md:translate-x-0 md:right-[420px] z-20 px-3 py-1.5 rounded-full bg-black/70 text-[#D4AF37] text-sm font-mono-sheet" data-testid="ruler-distance">
        {rulerDistanceFt} ft
        </div>
      )}
      {obstacleDialog && (
        <ObstacleCreateDialog
          open
          onOpenChange={(v) => !v && setObstacleDialog(null)}
          gameId={game.id}
          sceneId={scene?.id}
          pos={obstacleDialog.pos}
          points={obstacleDialog.points}
          existing={obstacleDialog.existing}
          onSaved={upsertObstacleLocal}
          onDeleted={removeObstacleLocal}
        />
      )}
      {arrivalPick && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-30 flex items-center gap-3 px-4 py-2 rounded-full bg-[#1E3A8A]/95 backdrop-blur-md border border-white/10 text-sm text-white shadow-lg" data-testid="arrival-pick-banner">
          <MapPin className="w-4 h-4 text-[#D4AF37]" />
          <span>Click anywhere on this map to place the arrival point</span>
          <button data-testid="cancel-arrival-pick-button" onClick={cancelArrivalPick} className="text-xs uppercase tracking-wider underline hover:text-[#D4AF37]">Cancel</button>
        </div>
      )}
      {doorDialog && (
        <DoorCreateDialog
          open={!arrivalPick}
          onOpenChange={(v) => !v && setDoorDialog(null)}
          gameId={game.id}
          game={game}
          sceneId={scene?.id}
          x={doorDialog.pos?.x}
          y={doorDialog.pos?.y}
          existing={doorDialog.existing}
          resetKey={doorDialog.existing?.id || `new-${doorDialog.pos?.x}-${doorDialog.pos?.y}`}
          onSaved={upsertDoorLocal}
          onDeleted={removeDoorLocal}
          onPickArrivalOnMap={startArrivalPick}
          pickedArrivalX={pickedArrival?.x}
          pickedArrivalY={pickedArrival?.y}
          onArrivalConsumed={() => setPickedArrival(null)}
          isPortal={doorDialog.isPortal || doorDialog.existing?.trigger_mode === "notify_gm" || doorDialog.existing?.trigger_mode === "auto" || doorDialog.existing?.is_visible === false}
        />
      )}
      {templateEditing && (
        <TemplateEditDialog
          open
          onOpenChange={(v) => !v && setTemplateEditing(null)}
          gameId={game.id}
          template={templateEditing}
          canEditEffect={isGm}
          gridSize={gridSize}
          onSaved={upsertTemplateLocal}
          onDeleted={removeTemplateLocal}
        />
      )}
      <DoorLockModal
        open={!!lockDoorTarget}
        onOpenChange={(v) => !v && setLockDoorTarget(null)}
        gameId={game.id}
        door={lockDoorTarget?.door}
        token={lockDoorTarget?.token}
        character={character}
      />
      <NpcInteractModal
        open={!!interactingNpc}
        onOpenChange={(v) => !v && closeNpc()}
        gameId={game.id}
        npcToken={tokens.find((t) => t.id === interactingNpc?.id) || interactingNpc}
        character={character}
        stats={stats}
        onCharacterUpdate={onCharacterUpdate}
        onAttack={(npc) => setAttackTarget(npc)}
      />
      <AttackModal
        open={!!attackTarget}
        onOpenChange={(v) => !v && setAttackTarget(null)}
        gameId={game.id}
        game={game}
        character={character}
        stats={stats}
        attackerToken={attackTarget ? findMyNearbyToken(attackTarget) : null}
        targetToken={tokens.find((t) => t.id === attackTarget?.id) || attackTarget}
        gridSize={gridSize}
        myMemberId={myMemberId}
        myUserName={character?.name || "A player"}
      />
      <ChestLootModal
        open={!!openingChest}
        onOpenChange={(v) => !v && setOpeningChest(null)}
        gameId={game.id}
        chestToken={tokens.find((t) => t.id === openingChest?.id) || openingChest}
        character={character}
        onCharacterUpdate={onCharacterUpdate}
      />
    </div>
  );
}
